<template>
    <MainContent title="Editar abonado">
        <EditarSkeleton v-if="loading" />

        <form v-else-if="abonado" @submit.prevent="submitForm">
            <ValidationObserver ref="observer" v-slot="{ invalid }">
                <Card>
                    <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                        <BaseInputText
                            label="Nº de abonado"
                            name="Número de abonado"
                            data-cy="n-abonado"
                            rules="required"
                            :value="abonado.n_abonado"
                            @input="val => onFieldChange('n_abonado', val)"
                        />

                        <BaseInputText
                            label="Nombre"
                            name="Nombre"
                            data-cy="nombre"
                            rules="required"
                            :value="abonado.name"
                            @input="val => onFieldChange('name', val)"
                        />
                        <BaseInputText
                            label="Apellidos"
                            name="Apellidos"
                            data-cy="apellidos"
                            rules="required"
                            :value="abonado.surnames"
                            @input="val => onFieldChange('surnames', val)"
                        />

                        <BaseInputText
                            label="DNI"
                            name="DNI"
                            data-cy="dni"
                            rules="required"
                            :value="abonado.dni"
                            @input="val => onFieldChange('dni', val)"
                        />
                        <BaseDateInput
                            label="Fecha de nacimiento"
                            name="fecha_nacimiento"
                            data-cy="fecha-nacimiento"
                            rules="required"
                            min="1900-01-01"
                            max="2999-12-31"
                            :value="abonado.fecha_nacimiento"
                            @input="val => onFieldChange('fecha_nacimiento', val)"
                        />

                        <BaseInputText
                            label="Teléfono"
                            name="Teléfono"
                            data-cy="telefono"
                            :value="abonado.telefono"
                            @input="val => onFieldChange('telefono', val)"
                        />
                        <BaseInputText
                            label="Móvil"
                            name="Móvil"
                            data-cy="movil"
                            :value="abonado.movil"
                            @input="val => onFieldChange('movil', val)"
                        />
                        <BaseInputText
                            label="Email"
                            name="Email"
                            type="email"
                            data-cy="email"
                            rules="email"
                            :value="abonado.email"
                            @input="val => onFieldChange('email', val)"
                        />

                        <BaseInputText
                            label="Código Postal"
                            name="Código Postal"
                            data-cy="codigo-postal"
                            :value="abonado.cp"
                            @input="val => onFieldChange('cp', val)"
                        />
                        <BaseInputText
                            label="Provincia"
                            name="Provincia"
                            data-cy="provincia"
                            :value="abonado.provincia"
                            @input="val => onFieldChange('provincia', val)"
                        />

                        <BaseInputText
                            label="Localidad"
                            name="Localidad"
                            data-cy="localidad"
                            :value="abonado.localidad"
                            @input="val => onFieldChange('localidad', val)"
                        />
                        <BaseInputText
                            label="Domicilio"
                            name="Domicilio"
                            data-cy="domicilio"
                            :value="abonado.domicilio"
                            @input="val => onFieldChange('domicilio', val)"
                        />

                        <BaseInputText
                            label="IBAN"
                            name="IBAN"
                            data-cy="iban"
                            :value="abonado.iban"
                            @input="val => onFieldChange('iban', val)"
                        />
                        <BaseInputText
                            label="Peña"
                            name="Peña"
                            data-cy="pena"
                            :value="abonado.penista"
                            @input="val => onFieldChange('penista', val)"
                        />

                        <BaseInputText
                            label="Empresa"
                            name="Empresa"
                            data-cy="empresa"
                            :value="abonado.empresa"
                            @input="val => onFieldChange('empresa', val)"
                        />
                    </div>

                    <!-- Observaciones - full width -->
                    <div class="tw:mt-4">
                        <BaseTextarea
                            label="Observaciones"
                            name="Observaciones"
                            data-cy="observaciones"
                            :value="abonado.observaciones"
                            rows="6"
                            @input="val => onFieldChange('observaciones', val)"
                        />
                    </div>

                    <!-- Actions -->
                    <div class="tw:flex tw:justify-end tw:gap-3 tw:mt-6">
                        <Button
                            variant="ghost"
                            @click="goBack"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            data-cy="guardar"
                            :disabled="submitting || invalid"
                            :loading="submitting"
                        >
                            {{ submitting ? 'Guardando...' : 'Guardar' }}
                        </Button>
                    </div>
                </Card>
            </ValidationObserver>
        </form>
    </MainContent>
</template>

<script setup>
    import { ref, getCurrentInstance } from 'vue';
    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue';
    import BaseDateInput from '@/modules/admin/ui/BaseDateInput.vue';
    import BaseTextarea from '@/modules/admin/ui/BaseTextarea.vue';
    import EditarSkeleton from '@/modules/admin/abonados/components/EditarSkeleton.vue';
    import { useEditarAbonado } from '@/composables/admin/abonados/useEditarAbonado';

    const props = defineProps({
        lista_id: { type: [String, Number], required: true },
        abonado_id: { type: [String, Number], required: true },
    });

    const instance = getCurrentInstance();
    const observer = ref(null);

    const {
        abonado,
        loading,
        submitting,
        submit,
    } = useEditarAbonado(props, {
        onSaved() {
            instance.proxy.$router.push({
                name: 'back.abonados.index',
                params: { lista_id: props.lista_id },
                query: instance.proxy.$route.query,
            });
        },
    });

    async function submitForm() {
        const isValid = await observer.value.validate();
        if (isValid) {
            submit();
        }
    }

    function onFieldChange(field, value) {
        if (abonado.value) {
            abonado.value[field] = value;
        }
    }

    function goBack() {
        instance.proxy.$router.push({
            name: 'back.abonados.index',
            params: { lista_id: props.lista_id },
            query: instance.proxy.$route.query,
        });
    }
</script>
